// Camera input the stock Cesium controller handles badly: laptop trackpads and the keyboard.
//
// Trackpad zoom. Cesium's wheel handling has two problems on a laptop (Cesium 1.142,
// CameraEventAggregator listenToWheel + ScreenSpaceCameraController.zoomEventTypes):
//   1. A trackpad PINCH arrives as a wheel event with ctrlKey set (Chrome, Edge, Firefox).
//      Cesium files that under WHEEL+CTRL, which no camera action listens to, and still calls
//      preventDefault - so pinching does nothing at all.
//   2. A trackpad two-finger scroll sends many small wheel events per frame. The aggregator
//      OVERWRITES its movement on each event instead of adding them up, so most of the
//      gesture is dropped, and what survives is then smeared by Cesium's zoom inertia on top
//      of the OS's own scroll momentum. That's the jumpy, laggy feel.
// So the wheel is taken off Cesium (zoomEventTypes keeps right-drag and touch pinch) and
// handled here: every event's delta is accumulated, eased in over a few frames, and the
// camera moves along the ray under the cursor, so the point you're pointing at stays put.
//
// Keyboard fly: W/S forward/back and A/D sideways (level with the ground, whatever the
// pitch), Q/E down/up, arrows turn and tilt, Shift goes faster. Speed scales with height
// above the ground so it works from 50 m to 50 km. Ignored while typing in a field.
import {
  Viewer,
  Cartesian2,
  Cartesian3,
  Cartographic,
  CameraEventType,
  Matrix4,
  Math as CMath,
} from "cesium";

// Wheel zoom strength, in natural-log zoom per normalised wheel pixel. A mouse notch is
// ~100 px, so 0.0022 is ~20% closer per notch (about what Cesium's default + inertia gave).
const WHEEL_K = 0.0022;
// Trackpad pinch: Chrome reports pinch as ctrl+wheel with deltaY = -100 * ln(scale), so
// 0.01 tracks the fingers 1:1; a little more makes a single pinch cover more ground.
const PINCH_K = 0.014;
const EASE = 0.35; // share of the pending zoom applied per frame
const MIN_TARGET_DIST = 8; // never closer than this to the point under the cursor (m)
const MAX_HEIGHT = 2.0e7; // zoom-out ceiling (m above the ellipsoid)
const MIN_CLEARANCE = 3; // keep the camera this far above the ground (m)

function editableTarget(e: Event): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable;
}

/** Height of the camera above the ground (terrain if loaded, else the ellipsoid). */
function clearance(viewer: Viewer): number {
  const carto = viewer.camera.positionCartographic;
  const ground = viewer.scene.globe.getHeight(carto) ?? 0;
  return carto.height - ground;
}

/** Push the camera back up if a move left it under (or skimming) the ground. */
function keepAboveGround(viewer: Viewer): void {
  const carto = viewer.camera.positionCartographic;
  const ground = viewer.scene.globe.getHeight(carto);
  if (ground === undefined || carto.height >= ground + MIN_CLEARANCE) return;
  const lifted = Cartesian3.fromRadians(carto.longitude, carto.latitude, ground + MIN_CLEARANCE);
  Cartesian3.clone(lifted, viewer.camera.position);
}

/** The camera methods below work in world coordinates; drop any lookAt frame first. */
function worldFrame(viewer: Viewer): void {
  if (!Matrix4.equals(viewer.camera.transform, Matrix4.IDENTITY)) {
    viewer.camera.lookAtTransform(Matrix4.IDENTITY);
  }
}

export function installWheelZoom(viewer: Viewer): void {
  const scene = viewer.scene;
  const ctrl = scene.screenSpaceCameraController;
  ctrl.zoomEventTypes = [CameraEventType.RIGHT_DRAG, CameraEventType.PINCH];
  const canvas = viewer.canvas;

  let pending = 0; // log-zoom still to apply; + = in
  let target: Cartesian3 | undefined;
  let lastPos = new Cartesian2(-1e9, -1e9);
  let lastEventAt = 0;
  let raf = 0;

  /** World point under a canvas position: a model or terrain (depth), else the globe. */
  const pickTarget = (pos: Cartesian2): Cartesian3 | undefined => {
    if (scene.pickPositionSupported) {
      const p = scene.pickPosition(pos);
      if (p && Number.isFinite(p.x)) return p;
    }
    const ray = viewer.camera.getPickRay(pos);
    const g = ray ? scene.globe.pick(ray, scene) : undefined;
    return g ?? viewer.camera.pickEllipsoid(pos) ?? undefined;
  };

  /** Re-pick when a new gesture starts or the cursor has moved, not on every event. */
  const aimAt = (clientX: number, clientY: number): void => {
    const rect = canvas.getBoundingClientRect();
    const pos = new Cartesian2(clientX - rect.left, clientY - rect.top);
    const now = performance.now();
    if (now - lastEventAt > 250 || Cartesian2.distance(pos, lastPos) > 4) {
      target = pickTarget(pos);
      lastPos = pos;
    }
    lastEventAt = now;
  };

  const zoomStep = (s: number): void => {
    worldFrame(viewer);
    const cam = viewer.camera;
    const h = Math.max(clearance(viewer), 1);
    let dir: Cartesian3;
    let dist: number;
    if (target) {
      dir = Cartesian3.subtract(target, cam.positionWC, new Cartesian3());
      dist = Cartesian3.magnitude(dir);
      if (dist < 1e-6) return;
      Cartesian3.divideByScalar(dir, dist, dir);
    } else {
      dir = Cartesian3.clone(cam.directionWC); // cursor on the sky: zoom along the view
      dist = h * 2;
    }
    // Looking across the landscape, the point under the cursor can be tens of km away;
    // step by a distance tied to the height so one notch doesn't leap to the horizon.
    const basis = Math.min(dist, Math.max(h, 30) * 10);
    let move = basis * (1 - Math.exp(-s)); // < 0 when zooming out
    if (target && move > 0) move = Math.min(move, dist - MIN_TARGET_DIST);
    if (move > 0 && !target) move = Math.min(move, h - MIN_CLEARANCE);
    if (move < 0 && cam.positionCartographic.height > MAX_HEIGHT) return;
    if (move === 0 || !Number.isFinite(move)) return;
    if (move < 0 && target === undefined) move = Math.max(move, -h); // sky: back off gently
    cam.move(dir, move);
    keepAboveGround(viewer);
  };

  const tick = (): void => {
    raf = 0;
    if (Math.abs(pending) < 1e-4) {
      pending = 0;
      return;
    }
    const s = Math.abs(pending) < 0.004 ? pending : pending * EASE;
    pending -= s;
    zoomStep(s);
    scene.requestRender();
    raf = requestAnimationFrame(tick);
  };
  const kick = (): void => {
    if (!raf) raf = requestAnimationFrame(tick);
  };

  canvas.addEventListener(
    "wheel",
    (e: WheelEvent) => {
      e.preventDefault(); // no page scroll, no browser zoom on pinch
      // Scrolling mid-flight takes over from the flight. Cesium switches enableInputs off for
      // the length of a flyTo and back on when it's cancelled, so cancel BEFORE the check;
      // what's left switching inputs off is the markup pen, which should block zoom.
      viewer.camera.cancelFlight();
      if (!ctrl.enableInputs || !ctrl.enableZoom) return;
      let dy = e.deltaY;
      if (e.deltaMode === 1) dy *= 33; // lines (Firefox mouse wheel)
      else if (e.deltaMode === 2) dy *= canvas.clientHeight; // pages
      if (!dy) return;
      // A trackpad pinch is ctrl+wheel with small deltas; Ctrl + a real mouse wheel
      // (|dy| ~100 per notch) zooms at the normal wheel rate.
      const pinch = e.ctrlKey && Math.abs(dy) < 50;
      aimAt(e.clientX, e.clientY);
      pending += -CMath.clamp(dy, -300, 300) * (pinch ? PINCH_K : WHEEL_K);
      pending = CMath.clamp(pending, -4, 4);
      kick();
    },
    { passive: false },
  );

  // Safari on a Mac reports trackpad pinch as gesture events, not ctrl+wheel.
  type GestureLike = Event & { scale: number; clientX: number; clientY: number };
  let gestureScale = 1;
  canvas.addEventListener("gesturestart", (e) => {
    e.preventDefault();
    gestureScale = 1;
    const g = e as GestureLike;
    lastEventAt = 0; // force a fresh pick for the new gesture
    aimAt(g.clientX, g.clientY);
  });
  canvas.addEventListener("gesturechange", (e) => {
    e.preventDefault();
    const g = e as GestureLike;
    viewer.camera.cancelFlight();
    if (!ctrl.enableInputs || !ctrl.enableZoom || !(g.scale > 0)) return;
    pending += Math.log(g.scale / gestureScale);
    gestureScale = g.scale;
    kick();
  });
  canvas.addEventListener("gestureend", (e) => e.preventDefault());
}

const MOVE_KEYS = new Set([
  "KeyW", "KeyA", "KeyS", "KeyD", "KeyQ", "KeyE",
  "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
]);

export function installKeyboardFly(viewer: Viewer): void {
  const scene = viewer.scene;
  const held = new Set<string>();
  let fast = false;
  let raf = 0;
  let last = 0;

  const step = (now: number): void => {
    if (held.size === 0) {
      raf = 0;
      return;
    }
    const dt = Math.min((now - last) / 1000, 0.1); // a stalled tab mustn't teleport you
    last = now;
    worldFrame(viewer);
    const cam = viewer.camera;
    const boost = fast ? 3 : 1;

    // Turn / tilt (arrows): rebuild the orientation from heading + pitch with no roll, so
    // turning never tips the horizon the way camera.lookLeft does when pitched down.
    let heading = cam.heading;
    let pitch = cam.pitch;
    const turn = CMath.toRadians(60) * boost * dt;
    const tilt = CMath.toRadians(40) * boost * dt;
    if (held.has("ArrowLeft")) heading -= turn;
    if (held.has("ArrowRight")) heading += turn;
    if (held.has("ArrowUp")) pitch += tilt;
    if (held.has("ArrowDown")) pitch -= tilt;
    pitch = CMath.clamp(pitch, CMath.toRadians(-89.9), CMath.toRadians(30));
    if (heading !== cam.heading || pitch !== cam.pitch) {
      cam.setView({ orientation: { heading, pitch, roll: 0 } });
    }

    // Move (WASD level with the ground, Q/E straight down/up). Directions come from the
    // local east/north/up frame and the heading, so W still means "forward" when you're
    // looking straight down at the site.
    const fwd = (held.has("KeyW") ? 1 : 0) - (held.has("KeyS") ? 1 : 0);
    const side = (held.has("KeyD") ? 1 : 0) - (held.has("KeyA") ? 1 : 0);
    const vert = (held.has("KeyE") ? 1 : 0) - (held.has("KeyQ") ? 1 : 0);
    if (fwd || side || vert) {
      const speed = CMath.clamp(clearance(viewer), 20, 50000) * 0.6 * boost; // m/s
      const pos = cam.positionWC;
      const up = scene.globe.ellipsoid.geodeticSurfaceNormal(pos, new Cartesian3());
      const east = Cartesian3.normalize(Cartesian3.cross(Cartesian3.UNIT_Z, up, new Cartesian3()), new Cartesian3());
      const north = Cartesian3.cross(up, east, new Cartesian3());
      const h = cam.heading;
      const forward = Cartesian3.add(
        Cartesian3.multiplyByScalar(north, Math.cos(h), new Cartesian3()),
        Cartesian3.multiplyByScalar(east, Math.sin(h), new Cartesian3()),
        new Cartesian3(),
      );
      const right = Cartesian3.add(
        Cartesian3.multiplyByScalar(east, Math.cos(h), new Cartesian3()),
        Cartesian3.multiplyByScalar(north, -Math.sin(h), new Cartesian3()),
        new Cartesian3(),
      );
      const delta = new Cartesian3();
      Cartesian3.add(delta, Cartesian3.multiplyByScalar(forward, fwd, new Cartesian3()), delta);
      Cartesian3.add(delta, Cartesian3.multiplyByScalar(right, side, new Cartesian3()), delta);
      Cartesian3.add(delta, Cartesian3.multiplyByScalar(up, vert, new Cartesian3()), delta);
      const len = Cartesian3.magnitude(delta);
      if (len > 0) cam.move(Cartesian3.divideByScalar(delta, len, delta), speed * dt);
      if (cam.positionCartographic.height > MAX_HEIGHT) {
        const c = Cartographic.clone(cam.positionCartographic);
        Cartesian3.clone(Cartesian3.fromRadians(c.longitude, c.latitude, MAX_HEIGHT), cam.position);
      }
      keepAboveGround(viewer);
    }
    scene.requestRender();
    raf = requestAnimationFrame(step);
  };

  window.addEventListener("keydown", (e) => {
    fast = e.shiftKey;
    if (!MOVE_KEYS.has(e.code)) return;
    if (e.ctrlKey || e.metaKey || e.altKey || editableTarget(e)) return;
    if (document.querySelector(".help-overlay.show")) return; // a modal is up
    e.preventDefault(); // arrows would otherwise scroll the sidebar
    if (held.has(e.code)) return; // key repeat
    held.add(e.code);
    viewer.camera.cancelFlight();
    if (!raf) {
      last = performance.now();
      raf = requestAnimationFrame(step);
    }
  });
  window.addEventListener("keyup", (e) => {
    fast = e.shiftKey;
    held.delete(e.code);
  });
  // Releasing a key while the window is in the background never fires keyup.
  const releaseAll = (): void => held.clear();
  window.addEventListener("blur", releaseAll);
  document.addEventListener("visibilitychange", releaseAll);
}
